import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { ChevronRight, Home } from 'lucide-react';

interface BreadcrumbItem {
  name: string;
  url?: string;
}

interface BreadcrumbNavProps {
  items: BreadcrumbItem[];
  className?: string;
}

/**
 * BreadcrumbNav - SEO-optimized breadcrumb navigation
 * Includes:
 * - Visible UI breadcrumbs for users
 * - JSON-LD schema for search engines
 * - Proper ARIA labels for accessibility
 * - Multi-language support
 */
const BreadcrumbNav: React.FC<BreadcrumbNavProps> = ({ items, className = '' }) => {
  const { language } = useLanguage();
  const BASE_URL = 'https://thegrassdepotllc.com';
  
  // Always include home as first item
  const homeItem: BreadcrumbItem = {
    name: language === 'es' ? 'Inicio' : 'Home',
    url: `/${language}`
  };
  
  const allItems = [homeItem, ...items];
  
  // Generate breadcrumb schema
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": allItems.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url ? `${BASE_URL}${item.url}` : undefined
    }))
  };

  return (
    <>
      <nav 
        aria-label={language === 'es' ? 'Navegación de migas de pan' : 'Breadcrumb navigation'}
        className={`flex items-center gap-2 text-sm ${className}`}
      >
        <ol className="flex items-center gap-2 flex-wrap" itemScope itemType="https://schema.org/BreadcrumbList">
          {allItems.map((item, index) => (
            <li 
              key={index} 
              className="flex items-center gap-2"
              itemProp="itemListElement" 
              itemScope 
              itemType="https://schema.org/ListItem"
            >
              {index > 0 && (
                <ChevronRight className="w-4 h-4 text-muted-foreground" aria-hidden="true" />
              )}
              
              {item.url && index < allItems.length - 1 ? (
                <Link 
                  to={item.url}
                  itemProp="item"
                  className="flex items-center gap-1 text-muted-foreground hover:text-primary transition-colors"
                >
                  {index === 0 && <Home className="w-4 h-4" aria-hidden="true" />}
                  <span itemProp="name">{item.name}</span>
                </Link>
              ) : (
                <span 
                  className="text-foreground font-medium"
                  itemProp="name"
                  aria-current="page"
                >
                  {item.name}
                </span>
              )}
              <meta itemProp="position" content={String(index + 1)} />
            </li>
          ))}
        </ol>
      </nav>
      
      {/* Breadcrumb Schema JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
    </>
  );
};

export default BreadcrumbNav;
